const RE_DECIMAL_PLACES = /^([%$:\d/]+)?0,?0(\.\[?(0*)\]?)%?$/;
/**
 * @enum {string}
 * @property CURRENCY
 * @property DURATION
 * @property PERCENT
 * @property NUMBER
 * @property PERCENT_SUFFIX
 * @property PER_100K
 * @property NONE
 */
export var FormatType;
(function (FormatType) {
    FormatType["CURRENCY"] = "$0,0";
    FormatType["DURATION"] = "0:00";
    FormatType["PERCENT"] = "0,0%";
    FormatType["NUMBER"] = "0,0";
    FormatType["PERCENT_SUFFIX"] = "/0,0%";
    FormatType["PER_100K"] = "100000/0,0";
    FormatType["NONE"] = "00";
})(FormatType || (FormatType = {}));
// String enums require a reverse mapping to be generated
FormatType[FormatType.CURRENCY] = FormatType.CURRENCY;
FormatType[FormatType.DURATION] = FormatType.DURATION;
FormatType[FormatType.PERCENT] = FormatType.PERCENT;
FormatType[FormatType.NUMBER] = FormatType.NUMBER;
FormatType[FormatType.PERCENT_SUFFIX] = FormatType.PERCENT_SUFFIX;
FormatType[FormatType.PER_100K] = FormatType.PER_100K;
FormatType[FormatType.NONE] = FormatType.NONE;
/**
 * Convert a number into a decimal place template
 * @param {string} numericFormat
 * @return {boolean}
 * @private
 */
function makeDecimalPlace(count) {
    return count <= 0 ? '' : `.${new Array(count).fill('0').join('')}`;
}
/**
 * Object recognizes the provided format and has a defined value
 * @param {string} numericFormat
 * @return {boolean}
 * @private
 */
function doesRecognize(numericFormat) {
    return this[numericFormat] !== undefined;
}
const NAMED_FORMAT_TYPE_MAP = {
    doesRecognize,
    number: FormatType.NUMBER,
    currency: FormatType.CURRENCY,
    percent: FormatType.PERCENT_SUFFIX,
    ratioAsPercent: FormatType.PERCENT,
    secondsAsDuration: FormatType.DURATION,
    none: FormatType.NONE,
};
const NAMED_FORMAT_DECIMAL_MAP = {
    doesRecognize,
    number: 0,
    currency: 0,
    percent: 1,
    ratioAsPercent: 1,
    secondsAsDuration: 0,
};
// Values must be supported by Intl.NumberFormat
const FORMATTER_STYLE_MAP = {
    [FormatType.CURRENCY]: 'currency',
    [FormatType.PERCENT]: 'percent',
    [FormatType.PERCENT_SUFFIX]: 'percent',
};
/**
 * Wrapper for numeric formatting. Relies on Intl.NumberFormat, but
 * augments with a few custom types (duration, etc).
 */
class NumericFormat {
    /**
     * Format provided value using the provided format
     * @param {UnformattedValue} value
     * @param {string} numericFormat
     * @return {FormattedValue}
     */
    static format(value, numericFormat) {
        if (value === null || value === undefined)
            return null;
        const { formatType, decimalPlaces } = NumericFormat.parseFormat(numericFormat);
        let raw = value;
        const localeOpts = {
            maximumFractionDigits: decimalPlaces,
            minimumFractionDigits: decimalPlaces,
        };
        // Transform raw values based on format needs
        if (formatType === FormatType.PERCENT_SUFFIX) {
            raw /= 100;
        }
        else if (formatType === FormatType.PER_100K) {
            raw *= 100000;
        }
        else if (formatType === FormatType.DURATION) {
            raw /= 60;
        }
        // Special format handling
        if (formatType === FormatType.NONE) {
            return String(raw);
        }
        else if (formatType === FormatType.DURATION) {
            const seconds = value % 60;
            const padded = seconds < 10 ? '0' : '';
            return `${Math.floor(raw)}:${padded}${seconds.toLocaleString(undefined, localeOpts)}`;
        }
        const formatter = new Intl.NumberFormat('en-US', {
            style: FORMATTER_STYLE_MAP[formatType],
            currency: 'USD',
            ...localeOpts
        });
        return formatter.format(raw);
    }
    /**
     * Format provided value as a number with the given decimal places
     * @param {UnformattedValue} value
     * @param {number} [decimalPlaces]
     * @return {FormattedValue}
     */
    static formatNumber(value, decimalPlaces = 0) {
        const format = NumericFormat.makeFormat(FormatType.NUMBER, decimalPlaces);
        return NumericFormat.format(value, format);
    }
    /**
     * Format provided value as a percentage with the given decimal places
     * @param {UnformattedValue} value
     * @param {number} decimalPlaces
     * @return {FormattedValue}
     */
    static formatPercent(value, decimalPlaces = 0) {
        const format = NumericFormat.makeFormat(FormatType.PERCENT, decimalPlaces);
        return NumericFormat.format(value, format);
    }
    /**
     * Construct a format type string from base type and number of decimal places
     * @param {FormatType} formatType
     * @param {number} decimalPlaces
     * @return {string}
     */
    static makeFormat(formatType, decimalPlaces = 0) {
        const suffix = makeDecimalPlace(decimalPlaces);
        let format = formatType + suffix;
        // Percentages requires special handling: 0,0%.0 -> 0,0.0%
        if ([FormatType.PERCENT, FormatType.PERCENT_SUFFIX].includes(formatType)) {
            format = `${formatType.replace('%', '') + suffix}%`;
        }
        return format;
    }
    /**
     * Parse apart both elements of a format (type & decimals)
     * @param {string} numericFormat
     * @return {{ formatType: FormatType, decimalPlaces: number }}
     */
    static parseFormat(numericFormat) {
        return {
            formatType: NumericFormat.parseFormatType(numericFormat),
            decimalPlaces: NumericFormat.parseDecimalPlaces(numericFormat),
        };
    }
    /**
     * Parse format type from template
     * @param {string} numericFormat
     * @return {FormatType}
     */
    static parseFormatType(numericFormat = FormatType.NUMBER) {
        let formatType = [
            FormatType.CURRENCY,
            FormatType.DURATION,
            FormatType.NUMBER,
            FormatType.NONE,
        ].find((f) => numericFormat.startsWith(f));
        formatType = numericFormat.endsWith('%') ? FormatType.PERCENT : formatType;
        formatType = numericFormat.startsWith('/') ? FormatType.PERCENT_SUFFIX : formatType;
        formatType = numericFormat.startsWith('%') ? FormatType.PERCENT : formatType;
        formatType = numericFormat.startsWith('100000/') ? FormatType.PER_100K : formatType;
        // Convert "named" formats into format templates
        if (formatType === undefined && NAMED_FORMAT_TYPE_MAP.doesRecognize(numericFormat)) {
            formatType = NAMED_FORMAT_TYPE_MAP[numericFormat];
        }
        return formatType || FormatType.NONE;
    }
    /**
     * Parse decimal places from template
     * @param {string} numericFormat
     * @return {number}
     */
    static parseDecimalPlaces(numericFormat = '') {
        let places = 0;
        const match = RE_DECIMAL_PLACES.exec(numericFormat);
        const groups = match && {
            full: match[0],
            decimalSegment: match[2],
            decimals: match[3],
        };
        if (groups && typeof groups.decimals === 'string') {
            places = groups.decimals.length;
        }
        // Convert "named" formats into implied decimal defaults
        if (NAMED_FORMAT_DECIMAL_MAP.doesRecognize(numericFormat)) {
            return NAMED_FORMAT_DECIMAL_MAP[numericFormat];
        }
        return places;
    }
}
NumericFormat.FormatType = FormatType;
export default NumericFormat;
